import React, { useMemo, useCallback, useState, useEffect } from "react";
import { GameBoard } from "./components/game-board.tsx";
import { GameControls } from "./components/game-controls.tsx";
import { useGame } from "./hooks/use-game.ts";
import { getDailyPuzzle, fetchDailyPuzzle, getPuzzleNumber } from "./lib/daily.ts";
import { loadStats, saveStats, recordCompletion } from "./lib/storage.ts";
import { totalFuel } from "./engine/grid.ts";
import type { GameState, Puzzle } from "./types.ts";

export function App({ onWin, embedded, winExtra, difficulty }: { onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode; difficulty?: string } = {}) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const puzzleNumber = useMemo(() => getPuzzleNumber(), []);

  useEffect(() => {
    setPuzzle(null);
    fetchDailyPuzzle(difficulty)
      .then(setPuzzle)
      .catch(() => setPuzzle(getDailyPuzzle(difficulty)));
  }, [difficulty]);

  if (!puzzle) {
    return (
      <div className={`flex flex-col items-center justify-center ${embedded ? 'p-4 min-h-[300px]' : 'min-h-screen p-6'} max-w-[700px] mx-auto select-none`}>
        <div className="animate-pulse text-slate-500 text-sm font-mono">Loading puzzle...</div>
      </div>
    );
  }

  return <GameInner puzzle={puzzle} puzzleNumber={puzzleNumber} onWin={onWin} embedded={embedded} winExtra={winExtra} />;
}

function GameInner({ puzzle, puzzleNumber, onWin, embedded, winExtra }: { puzzle: Puzzle; puzzleNumber: number; onWin?: (data: { stars: number; metric: number; timeMs: number }) => void; embedded?: boolean; winExtra?: React.ReactNode }) {
  const { state, selectStart, move, moveDir, undo, reset } = useGame(puzzle);
  const [showHelp, setShowHelp] = useState(false);
  const [stats, setStats] = useState(() => loadStats());

  const won = state.phase === "won";
  const stuck = state.phase === "stuck";
  const fuelRemaining = totalFuel(state.grid);

  useEffect(() => {
    if (won && !stats.history[puzzle.id]?.completed) {
      const timeMs = (state.completedAt ?? Date.now()) - state.startedAt;
      const updated = recordCompletion(stats, {
        puzzleId: puzzle.id,
        completed: true,
        stars: state.stars,
        steps: state.steps,
        timeMs,
      });
      setStats(updated);
      saveStats(updated);
      onWin?.({ stars: state.stars, metric: state.steps, timeMs });
    }
  }, [won]);

  return (
    <div className={`flex flex-col items-center ${embedded ? 'p-4 gap-4' : 'min-h-screen p-6 gap-6'} max-w-[700px] mx-auto select-none`}>
      {/* Header — hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between w-full">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <span className="text-orange-400">Burn</span>out
            </h1>
            <div className="flex gap-3 text-sm text-slate-500">
              <span>#{puzzleNumber}</span>
              <span className="text-slate-600">
                {puzzle.difficulty.name} ({puzzle.difficulty.rows}x{puzzle.difficulty.cols})
              </span>
              {stats.streak > 0 && (
                <span className="text-orange-400">{stats.streak} day streak</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {state.phase === "starting" && (
              <span className="text-xs text-orange-400 animate-pulse">
                Click a tile to start
              </span>
            )}
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white text-sm font-bold border border-slate-700"
            >
              ?
            </button>
          </div>
        </div>
      )}

      {/* Subheader when embedded */}
      {embedded && (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-slate-500">#{puzzleNumber}</span>
            <span className="font-mono text-xs text-slate-600">{puzzle.difficulty.name} ({puzzle.difficulty.rows}x{puzzle.difficulty.cols})</span>
            {state.phase === "starting" && (
              <span className="text-xs text-[#FF6B6B] animate-pulse">Click a tile to start</span>
            )}
            {stats.streak > 0 && (
              <span className="font-mono text-xs text-[#FF6B6B]">{stats.streak} day streak</span>
            )}
          </div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A1D33] text-slate-400 hover:text-white text-xs font-bold border border-[#2A2D42]"
          >
            ?
          </button>
        </div>
      )}

      {/* Help */}
      {showHelp && (
        <div className="w-full bg-slate-800/80 rounded-xl p-4 text-sm text-slate-300 border border-slate-700 space-y-2">
          <p className="font-medium text-white">How to play</p>
          <p>
            Burn out every tile on the grid by walking over them.
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              <strong>Click any tile</strong> to choose your starting position
            </li>
            <li>
              Move to <strong>adjacent tiles</strong> (click or arrow keys) — each
              step reduces that tile's fuel by 1
            </li>
            <li>
              When a tile reaches <strong className="text-orange-400">0</strong>, it
              burns out and can't be crossed again
            </li>
            <li>
              <strong>Goal:</strong> burn out{" "}
              <span className="text-orange-400">every tile</span> on the grid
            </li>
          </ol>
          <p className="text-slate-500">
            Starting position matters! Choose wisely for 3 stars.
          </p>
        </div>
      )}

      {/* Game board */}
      <GameBoard
        grid={state.grid}
        playerPos={state.playerPos}
        phase={state.phase}
        onSelectStart={selectStart}
        onMove={move}
        onMoveDir={moveDir}
      />

      {/* Controls */}
      <GameControls
        steps={state.steps}
        totalFuel={puzzle.totalFuel}
        fuelRemaining={fuelRemaining}
        canUndo={state.steps > 0 && !won}
        phase={state.phase}
        onMoveDir={moveDir}
        onUndo={undo}
        onReset={reset}
      />

      {/* Win */}
      {won && (
        <div className="w-full bg-orange-900/40 border border-orange-700 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <div className="text-4xl">
            {"\u2605".repeat(state.stars)}
            {"\u2606".repeat(3 - state.stars)}
          </div>
          <p className="text-xl font-bold text-orange-300">All Burned Out!</p>
          <p className="text-sm text-slate-400">
            {state.steps} step{state.steps !== 1 ? "s" : ""}
            {" \u2014 "}
            {state.stars === 3
              ? "Perfect path!"
              : state.stars === 2
                ? "Great job!"
                : "You got it!"}
          </p>
          <ShareButton state={state} puzzleNumber={puzzleNumber} />
          {stats.totalPlayed > 0 && !embedded && (
            <div className="pt-3 border-t border-orange-800 text-xs text-slate-500 flex justify-center gap-6">
              <span>
                Played:{" "}
                <strong className="text-slate-300">{stats.totalPlayed}</strong>
              </span>
              <span>
                Stars:{" "}
                <strong className="text-amber-400">{stats.totalStars}</strong>
              </span>
              <span>
                Best streak:{" "}
                <strong className="text-slate-300">{stats.maxStreak}</strong>
              </span>
            </div>
          )}
          {winExtra}
        </div>
      )}

      {/* Stuck */}
      {stuck && (
        <div className="w-full bg-red-900/30 border border-red-800 rounded-xl p-6 text-center space-y-3 animate-[fadeIn_0.3s_ease-out]">
          <p className="text-xl font-bold text-red-300">Stuck!</p>
          <p className="text-sm text-slate-400">
            No valid moves, but {fuelRemaining} fuel remains. Try a different path?
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={undo}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors"
            >
              Undo
            </button>
            <button
              onClick={reset}
              className="px-6 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareButton({
  state,
  puzzleNumber,
}: {
  state: GameState;
  puzzleNumber: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(() => {
    const text = [
      `\uD83D\uDD25 Burnout #${puzzleNumber}`,
      `${"\u2605".repeat(state.stars)}${"\u2606".repeat(3 - state.stars)} (${state.steps} step${state.steps !== 1 ? "s" : ""})`,
    ].join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [state, puzzleNumber]);

  return (
    <button
      onClick={handleShare}
      className="px-6 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg text-sm font-medium transition-colors"
    >
      {copied ? "Copied!" : "Share Result"}
    </button>
  );
}
